import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import MasterItemSummary from '@/components/master/MasterItemSummary';
import { devApi } from './devApi';
import { EngAssetFormDialog } from '@/pages/engineering/EngAssetFormDialog';

const MAKE_LABEL = { purchased: '購入品', in_house: '自社製作' };

const LEDGER_SCREEN = {
  mfg: '/mfg/items', eng: '/engineering/assets', office: '/bihin/items',
  equip: '/mfg/equipment', it: '/it/assets',
};

/**
 * 必要部品を追加 — 2段階だけ（1 品目を選ぶ → 2 1台あたりの必要数）。
 *
 * R3 fix (2026-10-21) · 既存の品目を選んだら、マスターに在るデータは**この画面では
 * 直せない**（読み取り専用のカードで見せ、直すのは「マスターを編集」から）。
 * 行で入力するのは 1台あたりの必要数 と 備考 だけ。製作区分もマスターから読む。
 * 自由入力のフォームは「新規品目として登録」のときだけ（先にマスターを作る）。
 */
export const AddPartDialog = ({ open, onOpenChange, devId, onAdded }) => {
  const [linked, setLinked] = useState(null);
  const [qtyPerUnit, setQtyPerUnit] = useState('1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setLinked(null); setQtyPerUnit('1'); setNotes('');
  }, [open]);

  // マスターを編集したあとに読み直す（品番で引き直して同じ品目を取り直す）。
  const refresh = async () => {
    if (!linked?.sku) return;
    try {
      const rows = await devApi.masterSearch(linked.sku);
      const same = (rows || []).find((r) => r.id === linked.id);
      if (same) { setLinked(same); toast.success('マスターの値を読み込み直しました'); }
    } catch { /* 読み直せなくても行の操作は続けられる */ }
  };

  const unlink = () => {
    setLinked(null);
    toast.message('品目の選択を外しました', { description: 'マスターからもう一度選んでください。' });
  };

  const makeType = linked?.make_type || '';

  const submit = async () => {
    if (!linked) {
      toast.error('品目をマスターから選んでください（未登録なら「新規品目として登録」）');
      return;
    }
    if (!makeType) {
      toast.error('製作区分が未登録です — マスターで設定してください');
      return;
    }
    if (!(Number(qtyPerUnit) > 0)) {
      toast.error('1台あたりの必要数を入力してください');
      return;
    }
    setBusy(true);
    try {
      await devApi.addParts(devId, [{
        source: linked.source === 'eng' ? 'eng_asset' : 'mfg_item',
        item_id: linked.id,
        name: linked.name,
        model_no: linked.model_no || null,
        drawing_no: linked.drawing_no || null,
        drawing_revision: linked.drawing_revision || null,
        maker: linked.maker || null,
        maker_pn: linked.maker_pn || null,
        material: linked.material || null,
        make_type: makeType,
        uom: linked.uom || '個',
        qty_per_unit: Number(qtyPerUnit),
        unit_cost: linked.last_price === null || linked.last_price === undefined
          ? null : Number(linked.last_price),
        supplier_id: linked.supplier_id || null,
        supplier_name: linked.supplier_name || null,
        category_id: linked.order_category_id || null,
        category_name: linked.order_category_name || null,
        reference_url: linked.reference_url || null,
        notes: notes.trim() || null,
      }]);
      toast.success('必要部品に追加しました');
      onAdded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '追加に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}
          data-testid="dev-add-part-dialog">
          <DialogHeader>
            <DialogTitle>必要部品を追加</DialogTitle>
            <p className="text-[11px] text-zinc-500" data-testid="dev-add-part-steps">
              <span className={linked ? 'text-zinc-400' : 'font-semibold text-teal-700'}>1. 品目を選ぶ</span>
              <span className="mx-1 text-zinc-300">›</span>
              <span className={linked ? 'font-semibold text-teal-700' : 'text-zinc-400'}>2. 1台あたりの必要数</span>
            </p>
          </DialogHeader>

          <div className="space-y-3">
            <MasterItemPicker
              testIdPrefix="dev-add-part"
              label="品目（マスターから選ぶ）"
              search={devApi.masterSearch}
              onPick={setLinked}
              onQueryChange={setQuery}
              linked={linked ? {
                label: linked.name, sku: linked.sku,
                sourceLabel: linked.source === 'eng' ? '技術資産' : '製造品目',
              } : null}
              onUnlink={unlink}
              unlinkLabel="別の品目を選び直す"
              emptyText="該当する品目がありません。品名の手入力はできません（標準ルール §7）。"
              emptyAction={(
                <Button type="button" variant="outline" className="h-9 w-full text-[12px]"
                  onClick={() => setNewItemOpen(true)} data-testid="dev-add-part-new-item">
                  新規品目として登録（品番は品番ルールから発番）
                </Button>
              )}
              helper="マスターの値を使います。未登録の部品は「新規品目として登録」で台帳に入れてから選んでください。"
            />

            {!linked && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
                data-testid="dev-add-part-must-pick">
                <p className="text-[12px] font-medium text-amber-900">
                  品目をマスターから選んでください（品名・型式・メーカーの手入力は廃止しました）。
                </p>
                <Button type="button" variant="outline" className="mt-2 h-9 text-[12px]"
                  onClick={() => setNewItemOpen(true)} data-testid="dev-add-part-new-item-banner">
                  新規品目として登録（品番は品番ルールから発番）
                </Button>
              </div>
            )}

            {linked && (
              <>
                <MasterItemSummary item={linked} testId="dev-add-part-master" onRefresh={refresh} />

                {!makeType && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"
                    data-testid="dev-add-part-make-missing">
                    <p className="text-[12px] font-medium text-rose-900">
                      製作区分が未登録です — マスターで設定してください
                    </p>
                    <a href={LEDGER_SCREEN[linked.source] || '/mfg/items'} target="_blank" rel="noreferrer"
                      className="mt-1 inline-block text-[11px] font-semibold text-teal-700 hover:text-teal-900"
                      data-testid="dev-add-part-make-missing-link">
                      マスターを編集
                    </a>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold text-zinc-700">
                    1台あたりの必要数
                    <span className="ml-1.5 rounded bg-rose-100 px-1 text-[10px] font-bold text-rose-700"
                      data-testid="dev-add-part-qty-req">必須</span>
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input type="number" min="0" step="any" value={qtyPerUnit}
                      onChange={(e) => setQtyPerUnit(e.target.value)}
                      className="h-10 w-32 tabular-nums" data-testid="dev-add-part-qty" />
                    <span className="text-sm text-zinc-600" data-testid="dev-add-part-uom-derived">
                      {linked.uom || '個'}
                      {makeType ? ` ／ ${MAKE_LABEL[makeType]}（マスターから）` : ''}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-zinc-500">1台つくるごとに消える数。</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-zinc-700">備考</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="例: 試作1号機で使用" className="mt-1 h-10" data-testid="dev-add-part-notes" />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dev-add-part-close">閉じる</Button>
            <Button onClick={submit} disabled={busy || !linked || !makeType}
              className="bg-teal-700 hover:bg-teal-800" data-testid="dev-add-part-submit">
              {busy ? '追加中…' : '追加する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 自由入力はここだけ。保存するとマスターが先にでき（品番ルールで発番）、
          その品目を選んだ状態で 2段階目に戻る。 */}
      <EngAssetFormDialog
        open={newItemOpen}
        onClose={() => setNewItemOpen(false)}
        prefill={{ name: query }}
        onSaved={(asset) => {
          setNewItemOpen(false);
          if (asset?.id) {
            setLinked({
              id: asset.id, source: 'eng', name: asset.name, sku: asset.sku || asset.asset_no,
              model_no: asset.model_no, maker: asset.maker, maker_pn: asset.maker_pn,
              drawing_no: asset.drawing_no, drawing_revision: asset.drawing_revision,
              material: asset.material, uom: asset.uom, reference_url: asset.reference_url,
              supplier_id: asset.supplier_id, supplier_name: asset.supplier_name,
              last_price: asset.unit_price,
              make_type: asset.acquisition_type === 'in_house' ? 'in_house' : 'purchased',
            });
          }
        }}
      />
    </>
  );
};

export default AddPartDialog;
